'use client';

import React, { createContext, useContext, useEffect, useState, useLayoutEffect } from 'react';
import { getCurrentUserInfo, checkAuthState, AuthUser, handleOAuthCallback, getAuthFlow, clearAuthFlow } from '@/lib/auth';
// Amplifyのハブでサインイン状態の変更を監視
import { Hub } from 'aws-amplify/utils';

// 認証状態の型定義（三状態管理）
type AuthenticationState = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  authState: AuthenticationState;
  refreshUser: () => Promise<void>;
  oauthCallbackResult: { 
    success: boolean; 
    isOAuthCallback: boolean; 
    flowType?: string;
    error?: Error;
    errorType?: string;
    errorDescription?: string;
  } | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authState, setAuthState] = useState<AuthenticationState>('loading');
  const [oauthCallbackResult, setOauthCallbackResult] = useState<{ 
    success: boolean; 
    isOAuthCallback: boolean; 
    flowType?: string;
    error?: Error;
    errorType?: string;
    errorDescription?: string;
  } | null>(null);
  const [isAmplifyInitialized, setIsAmplifyInitialized] = useState(false);

  // 後方互換性のための計算プロパティ
  const isLoading = authState === 'loading';
  const isAuthenticated = authState === 'authenticated';

  // LocalStorageからの事前チェック（高速判定）
  const preCheckAuthentication = (): boolean => {
    if (typeof window === 'undefined') return false;
    
    try {
      // Amplifyが保存するトークンの存在をチェック
      const clientId = '45p4c3k7a9qpmtbijs064kvir9';
      const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`;
      const lastAuthUser = localStorage.getItem(lastAuthUserKey);
      
      if (lastAuthUser) {
        // アクセストークンとIDトークンの存在をチェック
        const accessTokenKey = `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.accessToken`;
        const idTokenKey = `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.idToken`;
        
        const hasAccessToken = !!localStorage.getItem(accessTokenKey);
        const hasIdToken = !!localStorage.getItem(idTokenKey);
        const hasTokens = hasAccessToken && hasIdToken;
        
        console.log('事前認証チェック:', { 
          lastAuthUser, 
          hasAccessToken, 
          hasIdToken, 
          hasTokens 
        });
        
        return hasTokens;
      }
      
      console.log('事前認証チェック: LastAuthUserが見つかりません');
      return false;
    } catch (error) {
      console.error('事前認証チェックエラー:', error);
      return false;
    }
  };

  // ユーザー情報を更新する関数
  const refreshUser = async () => {
    try {
      console.log('認証状態更新開始');
      setAuthState('loading');
      
      const authStateResult = await checkAuthState();
      
      if (authStateResult.isAuthenticated) {
        const userInfo = await getCurrentUserInfo();
        setUser(userInfo);
        setAuthState('authenticated');
        console.log('認証状態: 認証済み', userInfo);
      } else {
        setUser(null);
        setAuthState('unauthenticated');
        console.log('認証状態: 未認証');
      }
    } catch (error) {
      console.error('ユーザー情報の更新エラー:', error);
      setUser(null);
      setAuthState('unauthenticated');
    }
  };

  // Amplify初期化の確認
  useLayoutEffect(() => {
    // Amplifyの初期化を待つ
    const checkAmplifyInit = () => {
      try {
        // Amplifyが初期化されているかチェック
        const isInit = typeof window !== 'undefined' && 
                      window.localStorage && 
                      document.readyState === 'complete';
        
        if (isInit) {
          console.log('Amplify初期化完了を確認');
          setIsAmplifyInitialized(true);
        } else {
          // 少し待ってから再チェック
          setTimeout(checkAmplifyInit, 50);
        }
      } catch (error) {
        console.error('Amplify初期化チェックエラー:', error);
        setIsAmplifyInitialized(true); // エラー時は初期化済みとして扱う
      }
    };

    // 開発環境では即座に初期化済みとして扱う
    if (process.env.NODE_ENV === 'development') {
      console.log('開発環境: Amplify初期化をスキップ');
      setIsAmplifyInitialized(true);
    } else {
      checkAmplifyInit();
    }
  }, []);

  useEffect(() => {
    if (!isAmplifyInitialized) {
      console.log('Amplify初期化待機中...');
      return;
    }

    // OAuth認証コールバックをチェック（初回のみ）
    const checkOAuthCallback = async () => {
      try {
        const callbackResult = await handleOAuthCallback();
        const flowType = getAuthFlow();
        
        if (callbackResult.isOAuthCallback) {
          console.log('OAuth認証コールバックを検出、フロータイプ:', flowType);
          setOauthCallbackResult({
            success: callbackResult.success,
            isOAuthCallback: callbackResult.isOAuthCallback,
            flowType: flowType || undefined,
            error: callbackResult.error,
            errorType: callbackResult.errorType,
            errorDescription: callbackResult.errorDescription
          });
          
          if (callbackResult.success) {
            // OAuth認証が成功した場合は即座に状態を更新
            await refreshUser();
            clearAuthFlow();
          } else {
            setAuthState('unauthenticated');
            clearAuthFlow(); // エラー時もフローをクリア
          }
          
          // OAuth認証結果を表示後、一定時間でクリアして無限ループを防ぐ
          setTimeout(() => {
            setOauthCallbackResult(null);
          }, 3000); // 3秒後にクリア
        } else {
          // 事前チェックで高速判定
          const hasTokens = preCheckAuthentication();
          
          if (hasTokens) {
            console.log('事前チェック: トークン存在、詳細認証チェック実行');
            await refreshUser();
          } else {
            console.log('事前チェック: トークンなし、未認証状態に設定');
            setAuthState('unauthenticated');
          }
        }
      } catch (error) {
        console.error('認証状態チェックエラー:', error);
        // エラー時は未認証状態に設定
        setAuthState('unauthenticated');
        setUser(null);
      }
    };

    checkOAuthCallback();
  }, [isAmplifyInitialized]); // authState を依存配列から削除

  // authState の変更に反応する別の useEffect（Hub リスナーとページ可視性変更のみ）
  useEffect(() => {
    if (!isAmplifyInitialized) {
      return;
    }

    // サインイン状態の変更を監視
    const hubListener = Hub.listen('auth', ({ payload }) => {
      console.log('認証イベント:', payload.event, payload);
      switch (payload.event) {
        case 'signInWithRedirect':
          console.log('OAuth認証が開始されました');
          break;
        case 'signInWithRedirect_failure':
          console.log('OAuth認証が失敗しました');
          setAuthState('unauthenticated');
          clearAuthFlow();
          break;
        case 'signedIn':
          console.log('ユーザーがサインインしました');
          refreshUser();
          break;
        case 'signedOut':
          console.log('ユーザーがサインアウトしました');
          setUser(null);
          setAuthState('unauthenticated');
          setOauthCallbackResult(null);
          clearAuthFlow();
          break;
        case 'tokenRefresh':
          console.log('トークンが更新されました');
          // 既に認証済みでローディング中でない場合のみ更新
          if (authState === 'authenticated') {
            refreshUser();
          }
          break;
        case 'tokenRefresh_failure':
          console.log('トークンの更新に失敗しました');
          setUser(null);
          setAuthState('unauthenticated');
          break;
        default:
          break;
      }
    });

    // ページの可視性変更時に認証状態を再確認
    const handleVisibilityChange = () => {
      if (!document.hidden && authState !== 'loading') {
        console.log('ページ可視化: 認証状態を再確認');
        refreshUser();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // クリーンアップ
    return () => {
      hubListener();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAmplifyInitialized, authState]);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    authState,
    refreshUser,
    oauthCallbackResult,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};